import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';

import { setMetadata as setMetadataAction } from 'state/insight/insight.slice';

import {
  ViewWrapper,
  Chip,
  ChipSkeleton,
} from 'components/partials/insight-modal/shared/components';
import {
  TitleField,
  DescriptionField,
} from 'components/partials/insight-modal/shared/form-fields';
import type { InsightFormValues } from 'components/partials/insight-modal/shared/form-fields';
import {
  INSIGHT_SOURCES,
} from 'components/partials/insight-modal/shared/constants';
import type { InsightModalMetadata } from 'components/partials/insight-modal/shared/constants';
import { useMonitoringGoals } from 'components/partials/insight-modal/monitoring/hooks';

interface MonitoringMetadataFormProps {
  initialMetadata?: InsightModalMetadata;
}

const MonitoringMetadataForm = ({ initialMetadata }: MonitoringMetadataFormProps) => {
  const dispatch = useDispatch();

  const { control, watch } = useForm<InsightFormValues>({
    defaultValues: {
      title: initialMetadata?.title || '',
      description: initialMetadata?.description || '',
    },
  });

  const criticalElement = initialMetadata?.criticalElement || '';

  const monitoringGoalsProps = {
    criticalElementId: initialMetadata?.criticalElementId,
    relatedGoals: Array.isArray(initialMetadata?.relatedGoals)
      ? initialMetadata.relatedGoals
      : [],
  };

  const { relatedGoals, isLoadingGoals } =
    useMonitoringGoals(monitoringGoalsProps);

  const title = watch('title');
  const description = watch('description');

  useEffect(() => {
    dispatch(
      setMetadataAction({
        title,
        description,
        criticalElement,
        criticalElementId: initialMetadata?.criticalElementId,
        relatedGoals,
        source: INSIGHT_SOURCES.MONITORING,
        summary: criticalElement,
      })
    );
  }, [
    title,
    description,
    criticalElement,
    relatedGoals,
    dispatch,
    initialMetadata?.criticalElementId,
  ]);

  return (
    <ViewWrapper>
      <div>
        <div className="text-navy-800 font-bold text-xl mb-3">Metadata</div>
        <div className="mb-3">
          <div className="text-gray-700 mb-1 font-semibold">
            Critical element
          </div>
          <Chip>{criticalElement || '—'}</Chip>
        </div>

        <div>
          <div className="text-gray-700 mb-1 font-semibold">Related goals</div>
          <div className="flex flex-wrap gap-2">
            {isLoadingGoals ? (
              <>
                <ChipSkeleton />
                <ChipSkeleton />
                <ChipSkeleton />
              </>
            ) : relatedGoals.length ? (
              relatedGoals.map((g, idx) => <Chip key={`${g}-${idx}`}>{g}</Chip>)
            ) : (
              <Chip>—</Chip>
            )}
          </div>
        </div>
      </div>

      <TitleField control={control} />
      <DescriptionField control={control} />
    </ViewWrapper>
  );
};

export default MonitoringMetadataForm;
